import React, { useState, useRef, useEffect } from 'react'
import useStore from 'state/knovStore'
import cn from 'classnames'
import styles from '../upvalue.module.scss'
import { SignalType } from 'types/signals'
import SatsIcon from 'components/shared/SatsIcon'
import UpvalueGlyph from 'components/votes/UpvalueGlyph'
import { toBitcoin, format, SATS_BEFORE_CONFIRM } from 'lib/bsv-util'
import { useSatsToDollars, formatUsdValue } from 'api/useBsvExchangeRate'
import { Answer } from 'types/answers'
import { Bitcoin, Sprout } from 'lucide-react'
import WithTooltip from 'components/shared/WithTooltip'
import { DEFAULT_UPVALUE_EMOJI, isDefaultUpvalueEmoji } from '../upvalueEmoji'
import { LARGE_UPVALUE_CONFIRM_MODAL_KEY } from '../upvalueModalKeys'
import { sameUserId } from 'state/freeReactionEligibility'
import type { ReactOrUpvalueOptions } from 'state/upvalueQueue'
import { signalButtonTooltip } from './signalButtonPresentation'
import { paidSignalTypeForTarget } from '../signalOwnership'

interface SignalButtonProps {
    signalType: SignalType
    showBoost: boolean
    useSats: boolean
    useUsd?: boolean
    quickLockRef: React.RefObject<HTMLDivElement>
    upvalueSats: number
    upvalueUsd?: number | null
    handleLockOrUpvalue: (
        sats: number,
        blocks?: number,
        type?: SignalType,
        options?: ReactOrUpvalueOptions,
    ) => void
    animateLock: (ref: React.RefObject<HTMLDivElement>, scale?: number) => void
    currentUser?: { id: string | number } | null
    answer: Answer
    containerRef?: React.RefObject<HTMLDivElement>
    onEffectTrigger?: (element: HTMLElement | null) => void
    clickPower?: number
    hasLeadingControls?: boolean
    activeReaction?: string | null
    nextPressIsFree?: boolean
    upvaluesEnabled?: boolean
    freeReactionAvailable?: boolean
    seed?: boolean
    alreadySeeded?: boolean
    minimumSeedSats?: number | null
    onOpenSignalOptions?: () => void
}

export function SignalButton({
    signalType,
    showBoost,
    useSats,
    useUsd = false,
    quickLockRef,
    upvalueSats,
    upvalueUsd = null,
    handleLockOrUpvalue,
    animateLock,
    currentUser,
    answer,
    containerRef,
    onEffectTrigger,
    clickPower = 0,
    hasLeadingControls = false,
    activeReaction,
    nextPressIsFree = false,
    upvaluesEnabled = true,
    freeReactionAvailable = false,
    seed = false,
    alreadySeeded = false,
    minimumSeedSats = null,
    onOpenSignalOptions,
}: SignalButtonProps) {
    const [glowIntensity, setGlowIntensity] = useState(0)
    const glowDecayRef = useRef<ReturnType<typeof setTimeout>>()
    const previousPaidIntroStateRef = useRef({ answerId: answer.id, nextPressIsFree })
    const [cumulativeAmount, setCumulativeAmount] = useState(0)
    const [showCumulative, setShowCumulative] = useState(false)
    const [showPaidIntro, setShowPaidIntro] = useState(false)
    const showAmount = !nextPressIsFree && upvaluesEnabled
    const ownPost = sameUserId(answer.user_id, currentUser?.id)
    const signalButtonTip = signalButtonTooltip({
        nextPressIsFree,
        upvaluesEnabled,
        freeReactionAvailable,
        seed,
        alreadySeeded,
        ownPost,
        showBoost,
    })

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (glowDecayRef.current) clearTimeout(glowDecayRef.current)
        }
    }, [])

    useEffect(() => {
        const previousState = previousPaidIntroStateRef.current
        const answerChanged = previousState.answerId !== answer.id
        const transitionedFromFreeToPaid =
            !answerChanged && previousState.nextPressIsFree && !nextPressIsFree && upvaluesEnabled
        previousPaidIntroStateRef.current = { answerId: answer.id, nextPressIsFree }

        if (!transitionedFromFreeToPaid) return

        setShowPaidIntro(true)
        const timeout = setTimeout(() => setShowPaidIntro(false), 900)
        return () => clearTimeout(timeout)
    }, [answer.id, nextPressIsFree, upvaluesEnabled])

    // Apply glow to container based on external click power
    useEffect(() => {
        if (containerRef?.current && clickPower > 0) {
            containerRef.current.classList.add(styles.containerGlow)
            containerRef.current.style.setProperty('--click-power', String(clickPower))
            // Show cumulative when effect is active
            if (cumulativeAmount > 0) {
                setShowCumulative(true)
            }
        }

        // When clickPower reaches 0, hide cumulative amount
        if (clickPower === 0 && showCumulative) {
            setShowCumulative(false)
            setCumulativeAmount(0)
        }

        return () => {
            if (containerRef?.current) {
                containerRef.current.classList.remove(styles.containerGlow)
            }
        }
    }, [clickPower, containerRef, showCumulative, cumulativeAmount])

    const handleUpvalueClick = () => {
        //console.log('handleUpvalueClick triggered', { signalType, isOwnAnswer: answer.user_id === currentUser.id })

        // Trigger the effect using the container or button element
        const element = containerRef?.current || quickLockRef.current
        if (onEffectTrigger) {
            onEffectTrigger(element)
        }

        if (signalType === 'upvalue' || signalType === 'boost') {
            // Update cumulative amount
            setCumulativeAmount(prev => prev + upvalueSats)
            setShowCumulative(true)

            // Remove the timeout - let effect timing control the reset

            // Increase glow intensity
            setGlowIntensity(prev => Math.min(prev + 0.2, 1))

            // Clear existing glow decay timeout
            if (glowDecayRef.current) clearTimeout(glowDecayRef.current)

            // Start glow decay after a delay
            glowDecayRef.current = setTimeout(() => {
                const decay = setInterval(() => {
                    setGlowIntensity(prev => {
                        if (prev <= 0) {
                            clearInterval(decay)
                            return 0
                        }
                        return prev - 0.05
                    })
                }, 50)
            }, 500)
        }
    }

    return (
        <div className={cn(styles.signalButtonComp, answer.bsv_lock_amt ? '' : styles.zero)}>
            <WithTooltip asChild tip={signalButtonTip}>
                <div
                    ref={quickLockRef}
                    data-testid={`signal-button-${signalType}`}
                    className={cn(
                        styles.quickSignalButton,
                        hasLeadingControls && styles.quickSignalButtonWithLeadingControls,
                        seed
                            ? styles.seedQuickButton
                            : showBoost
                            ? styles.boostQuickButton
                            : styles.upvalueQuickButton,
                        seed && alreadySeeded && styles.seedQuickButtonSeeded,
                        glowIntensity > 0 && styles.upvalueGlow,
                        showPaidIntro && styles.quickSignalButtonPaidIntro,
                    )}
                    style={
                        {
                            '--glow-intensity': glowIntensity,
                            '--click-power': clickPower,
                        } as React.CSSProperties
                    }
                    onClick={ev => {
                        ev.stopPropagation()
                        ev.preventDefault()

                        if (!currentUser?.id) {
                            useStore
                                .getState()
                                .actions.modalActions.openAuthModal(
                                    'signup',
                                    'upvalue_signal_button',
                                )
                            return
                        }

                        const reactionOptions: ReactOrUpvalueOptions | undefined =
                            activeReaction && !isDefaultUpvalueEmoji(activeReaction)
                                ? { reactionEmoji: activeReaction }
                                : undefined
                        // Free press (first reaction) or upvalues disabled for this
                        // stream: no money moves, so skip amount confirms and effects.
                        if (nextPressIsFree || (!upvaluesEnabled && freeReactionAvailable)) {
                            handleLockOrUpvalue(upvalueSats, 0, signalType, reactionOptions)
                            animateLock(quickLockRef, 1.1)
                            return
                        }
                        if (!upvaluesEnabled) return
                        if (seed && minimumSeedSats == null) {
                            onOpenSignalOptions?.()
                            return
                        }

                        const paidOptions: ReactOrUpvalueOptions = {
                            ...(reactionOptions || {}),
                            isSeed: seed,
                        }

                        // Ordinary self-upvalues become boosts; an explicit Seed keeps Seed semantics.
                        const answerSignalType = paidSignalTypeForTarget(signalType, ownPost, seed)
                        switch (answerSignalType) {
                            case 'upvalue':
                                handleUpvalueClick()
                                if (upvalueSats > SATS_BEFORE_CONFIRM) {
                                    useStore
                                        .getState()
                                        .actions.modalActions.openConfirmModal(
                                            `${seed ? 'Seed' : 'Upvalue'} ${toBitcoin(
                                                upvalueSats,
                                            )} bitcoin?`,
                                            seed ? 'Seed' : 'Upvalue',
                                            () =>
                                                handleLockOrUpvalue(
                                                    upvalueSats,
                                                    0,
                                                    answerSignalType,
                                                    paidOptions,
                                                ),
                                            {
                                                headerTitle: seed ? 'Seed' : 'Upvalue',
                                                modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY,
                                                iconType: 'upvalue',
                                                confirmVariant: 'bsv',
                                                disableOverlayBlur: true,
                                            },
                                        )
                                } else
                                    handleLockOrUpvalue(
                                        upvalueSats,
                                        0,
                                        answerSignalType,
                                        paidOptions,
                                    )
                                break
                            case 'boost':
                                handleUpvalueClick()
                                handleLockOrUpvalue(upvalueSats, 0, answerSignalType, paidOptions)
                                break
                        }
                        animateLock(quickLockRef, 1.1)
                    }}
                >
                    {!activeReaction && seed ? (
                        <Sprout
                            className={cn(
                                styles.mainButtonEmoji,
                                showPaidIntro && styles.mainButtonEmojiPaidIntro,
                            )}
                            size={17}
                            aria-hidden
                        />
                    ) : (
                        <UpvalueGlyph
                            emoji={activeReaction || DEFAULT_UPVALUE_EMOJI}
                            className={cn(
                                styles.mainButtonEmoji,
                                showPaidIntro && styles.mainButtonEmojiPaidIntro,
                            )}
                            size={20}
                        />
                    )}
                    {showAmount && (
                        <SignalButtonAmount
                            quickSats={showCumulative ? cumulativeAmount : upvalueSats}
                            quickUsd={showCumulative ? null : upvalueUsd}
                            useUsd={useUsd}
                            clickPower={clickPower}
                            contextStyles={showPaidIntro ? styles.amountIntro : undefined}
                        />
                    )}
                </div>
            </WithTooltip>
        </div>
    )
}

interface AmountProps {
    quickSats: number
    quickUsd?: number | null
    useUsd?: boolean
    contextStyles?: string
    clickPower?: number
}

function SignalButtonAmount({
    quickSats,
    quickUsd = null,
    useUsd = false,
    contextStyles,
    clickPower = 0,
}: AmountProps) {
    const convertedUsd = useSatsToDollars(useUsd && quickUsd == null ? quickSats : null)
    const usdValue = formatUsdValue(useUsd ? quickUsd ?? convertedUsd : null)
    const formattedSats = format(quickSats)
    const formattedBtc = toBitcoin(quickSats)
    const isSats = formattedSats.length <= formattedBtc.length
    const showUsd = useUsd && !!usdValue
    const displayStr = showUsd ? usdValue.replace(/^\$/, '') : isSats ? formattedSats : formattedBtc
    const showUsdPrefix = showUsd && usdValue.startsWith('$')

    return (
        <div
            className={cn(styles.amountLeft, contextStyles)}
            style={{
                transform: `scale(${1 + clickPower * 0.3})`,
                transition:
                    clickPower > 0
                        ? 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        : 'transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transformOrigin: 'center center',
                fontWeight: Math.min(500 + clickPower * 30, 900),
                zIndex: 1000 + clickPower,
            }}
        >
            <span className={styles.plusSign}>+</span>
            {showUsdPrefix ? (
                <span className={styles.lucideUsdIcon}>$</span>
            ) : showUsd ? null : isSats ? (
                <SatsIcon showBars={true} contextStyles={styles.lucideSatsIcon} />
            ) : (
                <Bitcoin className={styles.lucideBtcIcon} size={11} aria-hidden />
            )}

            <div className={cn(styles.quickBsvSmall, quickSats === 0 && styles.quickBsvSmallZero)}>
                {displayStr}
            </div>
        </div>
    )
}
